import React, {Fragment, useEffect, useRef, useState, useMemo, useContext} from "react";
import {useNavigate} from "react-router-dom";
import {GridColDef, GridFilterInputValueProps, GridFilterItem, GridFilterOperator} from "@mui/x-data-grid";
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import {ATTEndpoints} from "../../../../services/att/endpoints";
import WebURL from "../../../../urls";
import CustomDataGrid, {DataGridRef} from "../../../../components/data-grid/CustomDataGrid";
import {useTranslation} from "react-i18next";
import {Box, Grid, TextField, Paper, Typography, MenuItem, Select, FormControl, InputLabel, InputAdornment, Button} from "@mui/material";
import moment from "moment";
import SnackbarUtil from "@/utils/SnackbarUtil";
import Util from "@/utils/Util";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import ColumnSettingsDialog from "@/components/column-settings/index";
import {ColumnSettings, getColumnSettings, getDefaultColumnSettings} from "@/services/Template/TemplateService";
import request from "@/utils/Request";
import DepartmentService from "@/services/hr/DepartmentService";
import CompanyService from "@/services/auth/CompanyService";
import OvertimeAnalysisReportShare from "./OvertimeAnalysisReportShare";
import GenericService from "@/services/GenericService";
import ExportAction from "@/components/actions/Export";
import TimecardService from "@/services/att/services/TimecardService";
import {DataGridContext, DataGridDispatchContext} from "@/components/data-grid/DataGridContext";

interface Department {
  id: string;
  name: string;
  code: string;
}
import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";

const OvertimeAnalysisReport: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dataGridRef = useRef<DataGridRef | null>(null);
  const loadedRef = useRef<boolean>(false);
  const { page, pageSize } = useContext(DataGridContext);
  const reportShareRef = useRef<any>(null);

  // Date format from attendance settings
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy");

  useEffect(() => {
    const init = async () => {
      // Get date format first
      const dateRes = await CompanyService.dateFormat({ method: "get" });
      const date_format = dateRes.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);

      const fetchSettings = async () => {
        GenericService.list(ATTEndpoints.ATT_ATTENDANCE_SETTING_URI, {current: 0, pageSize: 100}).then((res) => {
          if (res.data && res.status === 200) {
            setPaymentStatus(res.data.subscription_status)
            setExportReport(res.data.export_report)

            // Check if overtime rule is enabled
            const overtimeRule = res.data.overtime_rule;
            if (overtimeRule && overtimeRule.enabled !== undefined) {
              setOvertimeRuleEnabled(overtimeRule.enabled);
            }
          }
        })
      };
      fetchSettings();
    };
    init();
  }, []);

  const [paymentStatus, setPaymentStatus] = useState<boolean>(false)
  const [exportReport, setExportReport] = useState<string>('0')

  // State for filter options
  const [payPeriods, setPayPeriods] = useState<any[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedDepartmentCode, setSelectedDepartmentCode] = useState<string>("");
  // Use ref to store search value to avoid stale closure in useMemo
  const searchKeywordRef = useRef<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // State for DataGrid
  const [gridColumn, setGridColumn] = useState<GridColDef[]>([]);
  const [filterParams, setFilterParams] = useState<any>({});
  const [dataRangeReady, setDataRangeReady] = useState<boolean>(false);

  // Helper function to build filter params with date_range
  const getFilterParams = (deptCode?: string, payPeriodId?: number, periods?: any[]) => {
    const periodId = payPeriodId !== undefined ? payPeriodId : (selectedPayPeriod ? parseInt(selectedPayPeriod) : undefined);
    const periodsList = periods || payPeriods;
    if (periodId !== undefined && periodsList.length > 0) {
      const selectedPeriod = periodsList.find(p => p.id === periodId);
      if (selectedPeriod) {
        const params: any = {
          date_range: [
            selectedPeriod.start_date_api,
            selectedPeriod.end_date_api
          ],
          all: true
        };
        // Only add department_code if a specific department is selected (not "All")
        // Use provided deptCode if available, otherwise read from state
        const code = deptCode !== undefined ? deptCode : selectedDepartmentCode;
        if (code && code !== "") {
          params.department_code = code;
        }
        return params;
      }
    }
    return { date_range: [] };
  };

  // State for statistics cards
  const [totalEmployees, setTotalEmployees] = useState<number>(0);
  const [t1Othours, setT1Othours] = useState<string>("");
  const [t2Othours, setT2Othours] = useState<string>("");
  const [otEmployees, setOtEmployees] = useState<number>(0);

  // State for overtime settings (T1/T2 Weekly OT thresholds)
  const [t1WeeklyOtThreshold, setT1WeeklyOtThreshold] = useState<number | null>(null);
  const [t2WeeklyOtThreshold, setT2WeeklyOtThreshold] = useState<number | null>(null);
  const [overtimeType, setovertimeType] = useState<string>(''); // 'daily' or other

  // State for overtime rule enabled check
  const [overtimeRuleEnabled, setOvertimeRuleEnabled] = useState<boolean>(true);

  // State for download/export
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportFormat, setExportFormat] = useState<string>('');

  // Risk Level Cell Renderer
  const RiskLevelField = (params: any) => {
    // Return empty string if no data
    if (!params.value) return '';

    // Get the color based on the value
    const valueLower = (params.value || '').toLowerCase();
    let color = '#999';
    if (valueLower === 'high') color = '#F44336';
    else if (valueLower === 'medium') color = '#FF9800';
    else if (valueLower === 'low') color = '#4CAF50';

    // Get translated text for the risk level
    const getRiskLevelText = () => {
      switch (valueLower) {
        case 'high': return t("common.Risk Level High");
        case 'medium': return t("common.Risk Level Medium");
        case 'low': return t("common.Risk Level Low");
        default: return params.value;
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '12px'}}>
          <Box sx={{width: '12px', height: '12px', backgroundColor: color, borderRadius: '50%', position: 'relative', opacity: 0.3}}></Box>
          <Box sx={{width: '8px', height: '8px', backgroundColor: color, borderRadius: '50%', position: 'absolute'}}></Box>
        </Box>
        <span style={{ color: color }}>{getRiskLevelText()}</span>
      </Box>
    );
  };

  // Default columns for the grid
  const defaultColumns: GridColDef[] = [
    {headerName: t("common.Person ID"), field: 'employee_code', width: 120, filterable: false},
    {headerName: t("common.Person Name"), field: 'employee_name', width: 160, filterable: false},
    {headerName: t("common.Department"), field: 'department_name', width: 160, filterable: false},
    {headerName: t("common.Total Work Time"), field: 'total_paid_work_deduct_break', width: 140, filterable: false},
    {headerName: t("common.T1 OT Hours"), field: 'ot1_hrs', width: 120, filterable: false, renderCell: (params: any) => <span style={{ color: '#2BA9DE' }}>{params.value}</span>},
    {headerName: t("common.T2 OT Hours"), field: 'ot2_hrs', width: 120, filterable: false, renderCell: (params: any) => <span style={{ color: '#d32f2f' }}>{params.value}</span>},
    {headerName: t("common.OT Ratio"), field: 'ot_ratio', width: 100, filterable: false},
    {headerName: t("common.Risk Level"), field: 'risk_level', width: 100, filterable: false, renderCell: RiskLevelField},
  ];

  // Load overtime settings (T1/T2 Weekly OT thresholds)
  useEffect(() => {
    const loadOvertimeSettings = async () => {
      try {
        const response = await request.authorizedAxios().get(ATTEndpoints.ATT_ATTENDANCE_SETTING_URI);
        const settings = response.data?.data;

        if (settings && settings.overtime_rule) {
          setT1WeeklyOtThreshold(settings.overtime_rule.t1);
          setT2WeeklyOtThreshold(settings.overtime_rule.t2);
          setovertimeType(settings.overtime_rule.overtime_type || '');
        }
      } catch (error) {
        console.error("Failed to load overtime settings:", error);
      }
    };

    loadOvertimeSettings();
  }, []);

  // Load column settings
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getColumnSettings("overtime_analysis");
        let list: ColumnSettings[] = res.data?.data || [];

        if (list.length === 0) {
          const def = await getDefaultColumnSettings("overtime_analysis");
          list = def as ColumnSettings[];
        }
        const active = list.find(x => x.is_active) || list[0];
        if (active) {
          // Parse template_field if it's a string (same logic as ColumnSettingsDialog)
          const cfgRaw = active.template_field;
          const cfg = typeof cfgRaw === 'string' ? JSON.parse(cfgRaw) : cfgRaw;
          const sortedConfig = [...cfg].sort((a, b) => (a.sort || 0) - (b.sort || 0));

          const final = sortedConfig
            .filter((x: any) => x.isCheck)
            .map((x: any) => defaultColumns.find(c => c.field === x.key))
            .filter((item): item is GridColDef => !!item);

          setGridColumn(final.length ? final : defaultColumns);
        }
      } catch (e) {
        setGridColumn(defaultColumns);
      } finally {
        // columnReady was removed as it was not being used
      }
    };
    load();
  }, [t]);

  // Load filter options (date ranges and departments)
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const loadFilterOptions = async () => {
      try {
        // Get date format first
        const dateRes = await CompanyService.dateFormat({ method: "get" });
        const date_format = dateRes.data?.date_format?.toString() || "MM/dd/yyyy";
        setDateFormat(date_format);

        // Get date ranges
        const dateRangesRes = await request.authorizedAxios().get(ATTEndpoints.ATT_GET_DATE_RANGE_URI);

        let periods: any[] = [];

        if (dateRangesRes && dateRangesRes.data && dateRangesRes.data.data) {
          const dateRanges = dateRangesRes.data.data.date_range || [];

          // Transform date ranges to pay periods format
          periods = dateRanges.map((range: any[], index: number) => {
            // The API returns dates in the format specified by dateFormat
            // Parse dates using moment with dateFormat to ensure correct conversion
            const parseFormat = date_format.replace('yyyy', 'YYYY').replace('dd', 'DD').replace('MM', 'MM');

            const startDateObj = moment(range[0], parseFormat);
            const endDateObj = moment(range[1], parseFormat);

            if (!startDateObj.isValid() || !endDateObj.isValid()) {
              return null;
            }

            const startDateFormatted = startDateObj.format('YYYY-MM-DD');
            const endDateFormatted = endDateObj.format('YYYY-MM-DD');

            return {
              id: index,
              name: `${range[0]} - ${range[1]}`,
              start_date: range[0],
              end_date: range[1],
              start_date_api: startDateFormatted,
              end_date_api: endDateFormatted
            };
          }).filter((item: any): item is NonNullable<typeof item> => item !== null);
          setPayPeriods(periods);
        }

        // Set filter params after pay periods are set
        if (periods.length > 0) {
          const firstPeriod = periods[0];
          setSelectedPayPeriod(firstPeriod.id.toString());
          // Set filter params first
          const params = getFilterParams(undefined, firstPeriod.id, periods);
          setFilterParams(params);
          // Set data range as ready after filter params are set
          setDataRangeReady(true);
        } else {
          // If no periods, still set data range as ready but with empty params
          // This allows the page to render without error
          setDataRangeReady(true);
        }

        // Get departments
        const deptRes = await DepartmentService.list({});
        if (deptRes && deptRes.data) {
          const items = deptRes.data.data || [];
          // Store department data with code mapping
          setDepartments(items);
          // Also create a map for quick lookup
          const deptMap: Record<string, string> = {};
          items.forEach((dept: Department) => {
            deptMap[dept.name] = dept.code;
          });
          // Set default selection to "All" (empty string) - no department filter by default
          setSelectedDepartmentCode("");
          setSelectedDepartment("");
        }
      } catch (e) {
        console.log()
      }
    };
    loadFilterOptions();
  }, []);

  // Note: Pay period changes are handled directly in the Select onChange handler
  // to avoid duplicate API requests
  // Refresh filter options (called when user clicks refresh button)
  const refreshFilterOptions = async () => {
    try {
      const dateRes = await request.authorizedAxios().get(ATTEndpoints.ATT_GET_DATE_RANGE_URI);
      if (dateRes && dateRes.data && dateRes.data.data) {
        const dateRanges = dateRes.data.data.date_range || [];

        // Transform date ranges to pay periods format
        const periods = dateRanges.map((range: any[], index: number) => {
          // The API returns dates in the format specified by dateFormat
          // Parse dates using moment with dateFormat to ensure correct conversion
          const parseFormat = dateFormat.replace('yyyy', 'YYYY').replace('dd', 'DD').replace('MM', 'MM');

          const startDateObj = moment(range[0], parseFormat);
          const endDateObj = moment(range[1], parseFormat);

          const startDateFormatted = startDateObj.format('YYYY-MM-DD');
          const endDateFormatted = endDateObj.format('YYYY-MM-DD');

          return {
            id: index,
            name: `${range[0]} - ${range[1]}`,
            start_date: range[0],
            end_date: range[1],
            start_date_api: startDateFormatted,
            end_date_api: endDateFormatted
          };
        });
        setPayPeriods(periods);

        // Set default selection for first pay period if available
        if (periods.length > 0) {
          const firstPeriod = periods[0];
          setSelectedPayPeriod(firstPeriod.id.toString());
          // Set filter params directly using the period ID - pass periods directly
          setFilterParams(getFilterParams(undefined, firstPeriod.id, periods));
        }
      }
    } catch (e) {
      console.error("Failed to refresh filter options:", e);
    }
  };

  // Load overtime settings (T1/T2 Weekly OT thresholds)
  useEffect(() => {
    const loadOvertimeSettings = async () => {
      try {
        const response = await request.authorizedAxios().get(ATTEndpoints.ATT_ATTENDANCE_SETTING_URI);
        const settings = response.data?.data;

        if (settings && settings.overtime_rule) {
          setT1WeeklyOtThreshold(settings.overtime_rule.t1);
          setT2WeeklyOtThreshold(settings.overtime_rule.t2);
        }
      } catch (error) {
        console.error("Failed to load overtime settings:", error);
      }
    };

    loadOvertimeSettings();
  }, []);

  const handleColumnsChange = (cfg: any[]) => {
    if (!cfg) return;
    const sortedConfig = [...cfg].sort((a, b) => (a.sort || 0) - (b.sort || 0));

    const finalColumns = sortedConfig
      .filter((item) => item.isCheck)
      .map((item) => defaultColumns.find((col) => col.field === item.key))
      .filter((item): item is GridColDef => !!item);

    setGridColumn(finalColumns.length ? finalColumns : defaultColumns);
    // Don't call refresh() here, let OriginDataGrid's useEffect handle it
  };

  const handleRefresh = () => {
    // Reset statistics before refresh
    setTotalEmployees(0);
    setT1Othours("");
    setT2Othours("");
    setOtEmployees(0);
    if (dataGridRef.current) {
      // Update filter params with current search value (always include keyword, even if empty)
      const params: any = {};
      if (selectedPayPeriod != null && payPeriods.length > 0) {
        const selectedPeriod = payPeriods.find(p => p.id === parseInt(selectedPayPeriod));
        if (selectedPeriod) {
          params.date_range = [
            selectedPeriod.start_date_api,
            selectedPeriod.end_date_api
          ];
          params.all = true;
        }
      }
      // Ensure date_range is always set - fallback to empty array if still not set
      if (!params.date_range) {
        params.date_range = [];
      }
      if (selectedDepartmentCode) params.department_code = selectedDepartmentCode;
      params.keyword = searchKeywordRef.current;
      setFilterParams(params);
      // Directly fetch with the new params to avoid multiple useEffect triggers
      if (dataGridRef.current) {
        dataGridRef.current.fetch();
      }
    }
  };

  const handleDepartmentChange = (newDeptCode: string) => {
    setSelectedDepartmentCode(newDeptCode);
    // Find the department by code and set its name
    const dept = departments.find(d => d.code === newDeptCode);
    if (dept) {
      setSelectedDepartment(dept.name);
    }
    // Update filter params and auto-refresh when department changes
    const params: any = {};
    if (selectedPayPeriod != null && payPeriods.length > 0) {
      const selectedPeriod = payPeriods.find(p => p.id === parseInt(selectedPayPeriod));
      if (selectedPeriod) {
        params.date_range = [
          selectedPeriod.start_date_api,
          selectedPeriod.end_date_api
        ];
        params.all = true;
      }
    }
    // Ensure date_range is always set - fallback to empty array if still not set
    if (!params.date_range) {
      params.date_range = [];
    }
    if (newDeptCode) params.department_code = newDeptCode;
    params.keyword = searchKeywordRef.current;
    setFilterParams(params);
    // Don't call refresh() here, let OriginDataGrid's useEffect handle it
  };

  const getOnRefresh = () => {
    return handleRefresh;
  };

  const FilterSection = useMemo(() => {
    return () => (
      <Paper sx={{p: 2, mb: 2, mt: 2, backgroundColor: '#ffffff', boxShadow: 0, border: 'none'}}>
        <Grid container={true} sx={{alignItems: "center", marginLeft: "5px"}}>
          <Grid item={true}>
            <FormControl size="small" sx={{width: "200px", mr: 2}}>
              <Box sx={{mb: 0.5}}>
                <Typography variant="body2" fontWeight="bold">{t("common.Pay Period")}</Typography>
              </Box>
              <Select
                value={selectedPayPeriod}
                onChange={(e) => {
                  const newPayPeriodId = e.target.value;
                  setSelectedPayPeriod(newPayPeriodId);
                  // Update filter params with current search value
                  const params: any = {};
                  if (newPayPeriodId != null && payPeriods.length > 0) {
                    const selectedPeriod = payPeriods.find(p => p.id === parseInt(newPayPeriodId));
                    if (selectedPeriod) {
                      params.date_range = [
                        selectedPeriod.start_date_api,
                        selectedPeriod.end_date_api
                      ];
                      params.all = true;
                    }
                  }
                  // Ensure date_range is always set - fallback to empty array if still not set
                  if (!params.date_range) {
                    params.date_range = [];
                  }
                  if (selectedDepartmentCode) params.department_code = selectedDepartmentCode;
                  params.keyword = searchKeywordRef.current;
                  setFilterParams(params);
                  // Don't call refresh() here, let OriginDataGrid's useEffect handle it
                }}
              >
                {payPeriods.map((pp) => (
                  <MenuItem key={pp.id} value={pp.id}>{pp.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item={true}>
            <FormControl size="small" sx={{width: "200px", mr: 2}}>
              <Box sx={{mb: 0.5}}>
                <Typography variant="body2" fontWeight="bold">{t("common.Department")}</Typography>
              </Box>
              <Select
                value={selectedDepartmentCode}
                displayEmpty
                renderValue={(value) => {
                  if (value === "") {
                    return <span>{t("common.All")}</span>;
                  }
                  const dept = departments.find(d => d.code === value);
                  return dept ? `${dept.code} - ${dept.name}` : value;
                }}
                onChange={(e) => {
                  const newDeptCode = e.target.value;
                  setSelectedDepartmentCode(newDeptCode);
                  // Find the department by code and set its name
                  const dept = departments.find(d => d.code === newDeptCode);
                  if (dept) {
                    setSelectedDepartment(dept.name);
                  }
                  // Update filter params and auto-refresh when department changes
                  const params: any = {};
                  if (selectedPayPeriod != null && payPeriods.length > 0) {
                    const selectedPeriod = payPeriods.find(p => p.id === parseInt(selectedPayPeriod));
                    if (selectedPeriod) {
                      params.date_range = [
                        selectedPeriod.start_date_api,
                        selectedPeriod.end_date_api
                      ];
                      params.all = true;
                    }
                  }
                  // Ensure date_range is always set - fallback to empty array if still not set
                  if (!params.date_range) {
                    params.date_range = [];
                  }
                  if (newDeptCode) params.department_code = newDeptCode;
                  params.keyword = searchKeywordRef.current;
                  setFilterParams(params);
                  // Don't call refresh() here, let OriginDataGrid's useEffect handle it
                }}
              >
                <MenuItem value="">
                  <span>{t("common.All")}</span>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.code} value={dept.code}>{dept.code} - {dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item={true}>
            <Box sx={{mb: 0.5, width: "200px"}}>
              <Typography variant="body2" fontWeight="bold">{t("common.Person Name")}</Typography>
            </Box>
            <TextField
              size="small"
              variant="outlined"
              id="person-search-input"
              defaultValue={searchKeyword}
              onChange={(e) => {
                const value = e.target.value;
                searchKeywordRef.current = value;
                setSearchKeyword(value);
              }}
              sx={{mr: 2, width: "200px"}}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const params: any = {};
                        // Get date_range from current filterParams if available
                        if (filterParams && filterParams.date_range && filterParams.date_range.length > 0) {
                          params.date_range = filterParams.date_range;
                          params.all = true;
                        } else if (selectedPayPeriod !== "" && payPeriods.length > 0) {
                          // Use selectedPayPeriod to find the period
                          const selectedPeriod = payPeriods.find(p => p.id === parseInt(selectedPayPeriod));
                          if (selectedPeriod) {
                            params.date_range = [
                              selectedPeriod.start_date_api,
                              selectedPeriod.end_date_api
                            ];
                            params.all = true;
                          }
                        }
                        // Ensure date_range is always set - fallback to empty array if still not set
                        if (!params.date_range) {
                          params.date_range = [];
                        }
                        if (selectedDepartmentCode) params.department_code = selectedDepartmentCode;
                        params.keyword = searchKeywordRef.current;
                        setFilterParams(params);
                        // Don't call refresh() here, let OriginDataGrid's useEffect handle it
                        if (dataGridRef.current) {
                          dataGridRef.current.search(searchKeywordRef.current);
                        }
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* T1 Weekly OT - Read Only with Lock Icon */}
          <Grid item={true}>
            <FormControl size="small" sx={{width: "200px", mr: 2}}>
              <Box sx={{mb: 0.5}}>
                <Typography variant="body2" fontWeight="bold">{t("common.T1 Weekly OT")}</Typography>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                value={overtimeType === 'daily' ? '' : (t1WeeklyOtThreshold !== null ? `${t1WeeklyOtThreshold} hrs` : '-')}
                InputProps={{
                  readOnly: true,
                  endAdornment: <LockIcon fontSize="small" sx={{color: 'text.secondary'}}>lock</LockIcon>,
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  backgroundColor: '#f5f5f5',
                }}
              />
              {/* Warning message for T1 outside 8-80 range */}
              {overtimeType !== 'daily' && t1WeeklyOtThreshold !== null && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                  {(t1WeeklyOtThreshold < 8 || t1WeeklyOtThreshold > 80) && (
                    <Box sx={{display: 'flex', alignItems: 'center', color: '#f57c00', fontSize: '12px'}}>
                      <i className="icon iconfont icon-ic_about" style={{fontSize: '14px', marginRight: '4px'}}></i>
                      <span>{t("common.T1 Weekly OT is outside the range of 8 to 80 hours")}</span>
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>
          </Grid>
          {/* T2 Weekly OT - Read Only with Lock Icon */}
          <Grid item={true}>
            <FormControl size="small" sx={{width: "200px", mr: 2}}>
              <Box sx={{mb: 0.5}}>
                <Typography variant="body2" fontWeight="bold">{t("common.T2 Weekly OT")}</Typography>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                value={overtimeType === 'daily' ? '' : (t2WeeklyOtThreshold !== null ? `${t2WeeklyOtThreshold} hrs` : '-')}
                InputProps={{
                  readOnly: true,
                  endAdornment: <LockIcon fontSize="small" sx={{color: 'text.secondary'}}>lock</LockIcon>,
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  backgroundColor: '#f5f5f5',
                }}
              />
              {/* Warning message for T2 outside 8-80 range */}
              {overtimeType !== 'daily' && t2WeeklyOtThreshold !== null && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                  {(t2WeeklyOtThreshold < 8 || t2WeeklyOtThreshold > 80) && (
                    <Box sx={{display: 'flex', alignItems: 'center', color: '#f57c00', fontSize: '12px'}}>
                      <i className="icon iconfont icon-ic_about" style={{fontSize: '14px', marginRight: '4px'}}></i>
                      <span>{t("common.T2 Weekly OT is outside the range of 8 to 80 hours")}</span>
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>
          </Grid>
          {/* Search icon is now inside Person Name text field */}
        </Grid>
      </Paper>
    );
  }, [selectedPayPeriod, selectedDepartmentCode, departments, payPeriods, overtimeRuleEnabled, t1WeeklyOtThreshold, t2WeeklyOtThreshold, overtimeType, exportReport, paymentStatus]);

  // Download data when exportFormat changes
  useEffect(() => {
    if (!exportFormat) return;

    const downloadData = async () => {
      try {
        // Build params with current filter values
        let params = '';

        if (selectedPayPeriod && payPeriods.length > 0) {
          const selectedPeriod = payPeriods.find(p => p.id === parseInt(selectedPayPeriod));
          if (selectedPeriod) {
            params = `date_range=${selectedPeriod.start_date_api}&date_range=${selectedPeriod.end_date_api}&all=true`;
          }
        }

        if (selectedDepartmentCode) {
          params += `&department_code=${selectedDepartmentCode}`;
        }

        const input = document.getElementById('person-search-input') as HTMLInputElement;
        const searchValue = input?.value || '';
        if (searchValue) {
          params += `&keyword=${searchValue}`;
        }

        // Build export meta data
        const export_meta = {
          fields: {
            employee_code: { alias: t("common.Person ID") },
            employee_name: { alias: t("common.Person Name") },
            department_name: { alias: t("common.Department") },
            total_paid_work_deduct_break: { alias: t("common.Total Work Time(h)") },
            ot1_hrs: { alias: t("common.T1 OT Hours") },
            ot2_hrs: { alias: t("common.T2 OT Hours") },
            ot_ratio: { alias: t("common.OT Ratio") },
            risk_level: { alias: t("common.Risk Level") }
          },
          title: t("common.Overtime Analysis Report"),
          file_type: exportFormat === 'xlsx with tz' || exportFormat === 'csv with tz' ? exportFormat.split(' ')[0] : exportFormat,
          share: []
        };

        TimecardService.download_data(params, export_meta, "overtime_analysis")
          .then((response) => {
            if (!response || !response.data) {
              throw new Error("Empty file response");
            }

            const contentType = response.headers?.["content-type"] || "application/octet-stream";
            const blob = new Blob([response.data], { type: contentType });

            // 异步执行，防止大文件卡死
            setTimeout(() => {
              const url = URL.createObjectURL(blob);
              const aLink = document.createElement("a");
              aLink.style.display = "none";
              aLink.href = url;

              // 自动读取文件名
              const disp = response.headers?.["content-disposition"];
              let fileName = "Overtime Analysis Report.xlsx";
              if (disp) {
                const fn = disp.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fn && fn[1]) {
                  fileName = decodeURIComponent(fn[1].replace(/['"]/g, ""));
                }
              }
              aLink.download = fileName;

              document.body.appendChild(aLink);
              aLink.click();

              setTimeout(() => {
                document.body.removeChild(aLink);
                URL.revokeObjectURL(url);
              }, 100);
            }, 0);

            SnackbarUtil.success(t("common.success"));
          })
          .catch((e) => {
            console.error("Download failed", e);
            SnackbarUtil.error(t("common.Download") + t("common.failure"), {
              anchorOrigin: { vertical: "top", horizontal: "center" }
            });
          })
          .finally(() => {
            setExportLoading(false);
            setExportFormat('');
          });
      } catch (e) {
        setExportLoading(false);
        setExportFormat('');
      }
    };

    setExportLoading(true);
    downloadData();
  }, [exportFormat, selectedPayPeriod, selectedDepartmentCode, t]);

  const toolbar = useMemo(() => ({
    title: t("common.Overtime Analysis Report"),
    breadcrumbs: [
      {breadcrumbName: t("common.Report"), path: WebURL.ATT_REPORT_HOME},
      {breadcrumbName: t("common.Overtime Analysis Report"), path: WebURL.ATT_REPORT_OVERTIME_ANALYSIS},
    ],
    searchTips: t("common.Search by Person ID, Person Name, Timesheet Name"),
    search: false,
    filter: false,
    actions: [
      {
        key: 'export',
        iconFont: 'icon-ic_export',
        helpTxt: t("export.Export"),
        callback: async () => {
          reportShareRef?.current?.showReport();
        }
      }
    ],
    refresh: true,
    onRefresh: () => handleRefresh,
    extraComponentOnRight:
      <Grid sx={{marginRight: '-15px'}}>
        <ColumnSettingsDialog
          report_type="overtime_analysis"
          onColumnsChange={handleColumnsChange}
        />
        <ExportAction
          setExportFormat={(fileType: string) => {
            setExportFormat(fileType);
            setExportLoading(true);
          }}
          loading={exportLoading}
          defaultFormat="xlsx"
        />
      </Grid>
  }), [t, handleRefresh, handleColumnsChange, exportLoading]);

  const renderStatisticsCards = () => {
    return (
      <Grid container={true} spacing={2} sx={{mb: 3, maxWidth: '80%', pl: 2}}>
        {/* Total Employees */}
        <Grid item={true} xs={6} sm={3} md={3}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#F4FAFD',
              boxShadow: 0,
              border: 'none'
            }}
          >
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("common.Total Employees")}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {totalEmployees !== -1 ? totalEmployees : '-'}
            </Typography>
          </Paper>
        </Grid>
        {/* T1 OT Hours */}
        <Grid item={true} xs={6} sm={3} md={3}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#F4FAFD',
              boxShadow: 0,
              border: 'none'
            }}
          >
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("common.T1 OT Hours")}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {t1Othours !== "-" ? t1Othours : '-'}
            </Typography>
          </Paper>
        </Grid>
        {/* T2 OT Hours */}
        <Grid item={true} xs={6} sm={3} md={3}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#F4FAFD',
              boxShadow: 0,
              border: 'none'
            }}
          >
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("common.T2 OT Hours")}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {t2Othours !== "-" ? t2Othours : '-'}
            </Typography>
          </Paper>
        </Grid>
        {/* OT Employees */}
        <Grid item={true} xs={6} sm={3} md={3}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#F4FAFD',
              boxShadow: 0,
              border: 'none'
            }}
          >
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("common.OT Employees")}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {otEmployees !== -1 ? otEmployees : '-'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Render placeholder when overtime rule is disabled
  const renderOvertimeDisabledPlaceholder = () => {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '30px 40px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #ccc',
        width: '80%',
        ml: '1%',
        mr: 'auto'
      }}>
        {/* Icon */}
        <Box sx={{
          width: '100px',
          height: '100px',
          backgroundColor: '#e3f2fd',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}>
          <PendingActionsOutlinedIcon sx={{fontSize: '48px', color: '#2196F3'}} />
        </Box>

        {/* Main message */}
        <Typography variant="h5" fontWeight="bold" sx={{mb: 2, color: '#333'}}>
          {t("common.Overtime report is not available")}
        </Typography>

        {/* Description */}
        <Typography variant="body2" sx={{color: '#777', mb: 3, textAlign: 'center'}}>
          {t("common.No overtime rule is applied to the selected pay period. Enable Overtime and configure an overtime rule to generate this report.")}
          <br />
        </Typography>

        {/* Set Up Overtime Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            navigate(WebURL.SETTING_ATTENDANCE + '?tab=overtime');
          }}
          sx={{
            mb: 3,
            px: 5,
            py: 1.2,
            borderRadius: '4px'
          }}
        >
          {t("common.Set Up Overtime")}
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{backgroundColor: '#ffffff', minHeight: '100vh'}}>
      <CustomBreadcrumbs record={[{label: t("common.Overtime Analysis Report")}]} />

      {/* Filter Section */}
      <FilterSection />

      {/* Show placeholder if overtime rule is disabled */}
      {!overtimeRuleEnabled && (
        <Box sx={{mt: 4}}>
          {renderOvertimeDisabledPlaceholder()}
        </Box>
      )}

      {/* Statistics Cards - Only show when overtime is enabled and data is ready */}
      {overtimeRuleEnabled && dataRangeReady && renderStatisticsCards()}

      {/* Data Grid - Only show when overtime is enabled and data is ready */}
      {overtimeRuleEnabled && dataRangeReady && (
        <CustomDataGrid
        columns={gridColumn}
        ref={dataGridRef}
        uri={ATTEndpoints.ATT_REPORT_OVERTIME_ANALYSIS}
        requestParams={filterParams}
        getRowId={(row: any) => row.employee_id || row.employee_code}
        toolbar={toolbar}
        localeText={{
          filterPanelColumns: t("common.Columns"),
          filterPanelOperators: t("common.Operators"),
        }}
        onDataChange={(data) => {
          // Data structure examples:
          // 1. Array format: [{ total_employees: 10, total_ot1_hours: 5, ... }, ...]
          // 2. Object format with data property: { total: 3, num_pages: 1, page: 1, page_size: 20, data: [...] }
          // 3. Response format: { status: 200, code: "I0000", message: "success.", data: { total: 0, num_pages: 1, page: 1, page_size: 20, data: [] } }

          let dataToProcess = data;

          // Handle response format with nested data property
          if (data && typeof data === 'object' && data.data && typeof data.data === 'object') {
            dataToProcess = data.data;
          }

          // Handle object format: { total: 3, num_pages: 1, page: 1, page_size: 20, data: [...] }
          if (dataToProcess && typeof dataToProcess === 'object' && !Array.isArray(dataToProcess)) {
            // Check if the data object itself contains the statistics
            if (dataToProcess.total_employees !== undefined) {
              setTotalEmployees(dataToProcess.total_employees);
            }
            if (dataToProcess.total_ot1_hours !== undefined) {
              setT1Othours(dataToProcess.total_ot1_hours);
            }
            if (dataToProcess.total_ot2_hours !== undefined) {
              setT2Othours(dataToProcess.total_ot2_hours);
            }
            if (dataToProcess.total_ot_employees !== undefined) {
              setOtEmployees(dataToProcess.total_ot_employees);
            }

            // Check if dataToProcess.data contains the statistics
            if (dataToProcess.data && Array.isArray(dataToProcess.data)) {
              if (dataToProcess.data.length > 0) {
                const item = dataToProcess.data[0];

                if (item && item.total_employees !== undefined) {
                  setTotalEmployees(item.total_employees);
                }
                if (item && item.total_ot1_hours !== undefined) {
                  setT1Othours(item.total_ot1_hours);
                }
                if (item && item.total_ot2_hours !== undefined) {
                  setT2Othours(item.total_ot2_hours);
                }
                if (item && item.total_ot_employees !== undefined) {
                  setOtEmployees(item.total_ot_employees);
                }
              } else {
                // dataToProcess.data is empty, reset statistics to "-"
                setTotalEmployees(-1);
                setT1Othours("-");
                setT2Othours("-");
                setOtEmployees(-1);
              }
            } else {
              // dataToProcess.data is not an array, reset statistics to "-"
              setTotalEmployees(-1);
              setT1Othours("-");
              setT2Othours("-");
              setOtEmployees(-1);
            }
          } else if (dataToProcess && Array.isArray(dataToProcess)) {
            // Get the first item which contains the summary data
            if (dataToProcess.length > 0) {
              const item = dataToProcess[0];

              if (item && item.total_employees !== undefined) {
                setTotalEmployees(item.total_employees);
              }
              if (item && item.total_ot1_hours !== undefined) {
                setT1Othours(item.total_ot1_hours);
              }
              if (item && item.total_ot2_hours !== undefined) {
                setT2Othours(item.total_ot2_hours);
              }
              if (item && item.total_ot_employees !== undefined) {
                setOtEmployees(item.total_ot_employees);
              }
            } else {
              // dataToProcess array is empty, reset statistics to "-"
              setTotalEmployees(-1);
              setT1Othours("-");
              setT2Othours("-");
              setOtEmployees(-1);
            }
          } else {
            // Data doesn't have the expected structure, reset statistics to "-"
            setTotalEmployees(-1);
            setT1Othours("-");
            setT2Othours("-");
            setOtEmployees(-1);
          }
        }}
        />
      )}
      <OvertimeAnalysisReportShare date_format={dateFormat} ref={reportShareRef} report_type="overtime_analysis" payment_status={paymentStatus} export_report={exportReport} />
      </Box>
  );
};

export default OvertimeAnalysisReport;
